import { Button, DialogContent, DialogTitle, ModalClose, ModalDialog } from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { ClimbType } from "griptonite-api-client";
import { TaskType } from "griptonite-api-client/types/tasks";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useCreateTaskMutation } from "@/hooks/tasks";

import { useCurrentClimbType, useCurrentVenueQuery } from "../../hooks/venues";
import { PillsSelectInput } from "../forms/PillsSelect";
import { DateInput } from "../forms/TextInput";
import { useModal } from "./useModal";

export const CreateTask = (_props: { routeUuids: string[] }) => {
  const { close } = useModal();

  const { data: venue } = useCurrentVenueQuery();
  const climbType = useCurrentClimbType();

  const climbTypeChoices = useMemo(() => {
    return venue?.climbTypes.map((climbType) => ({
      value: climbType,
      name: climbType === "BOULDER" ? "Bouldering" : "Sport",
    }));
  }, [venue?.climbTypes]);

  const { formState, ...methods } = useForm<{
    dueDate: string;
    type: TaskType;
    climbType: ClimbType;
  }>({
    defaultValues: {
      dueDate: format(new Date(), "yyyy-MM-dd"),
      type: "SET",
      climbType: climbType || venue?.climbTypes[0] || "BOULDER",
    },
  });

  const mutation = useCreateTaskMutation();
  const navigate = useNavigate();

  const onSubmit = async (data: { climbType: ClimbType; dueDate: string; type: TaskType }) => {
    if (!venue) {
      return;
    }
    const result = await mutation.mutateAsync({
      uuid: crypto.randomUUID(),
      venue: venue.uuid,
      climbType: data.climbType,
      dueDate: data.dueDate,
      type: data.type,
    });
    navigate({
      to: `/gym/$venueUuid/schedule/set/$setUuid/$taskUuid`,
      params: {
        venueUuid: result.venue,
        setUuid: result.set,
        taskUuid: result.uuid,
      },
      replace: true,
    });
    close();
  };

  const today = new Date();

  if (!venue || !climbTypeChoices) {
    return null;
  }

  return (
    <ModalDialog>
      <ModalClose />
      <DialogTitle>Create a task</DialogTitle>
      <DialogContent>
        <FormProvider formState={formState} {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <DateInput
              name="dueDate"
              minDate={format(today, "yyyy-MM-dd")}
              withTime={false}
              label="Due date"
            />
            {climbTypeChoices.length > 1 ? (
              <PillsSelectInput label="Climb type" name="climbType" options={climbTypeChoices} />
            ) : null}
            <PillsSelectInput
              label="Task type"
              name="type"
              options={
                [
                  { value: "SET", name: "Set" },
                  { value: "STRIP", name: "Strip" },
                  { value: "MISC", name: "Misc" },
                ] as const
              }
            />

            <Button
              sx={{ width: "100%", mt: 1 }}
              type="submit"
              loading={formState.isSubmitting}
              loadingPosition="start"
            >
              {formState.isSubmitting ? "Creating task..." : "Create"}
            </Button>
          </form>
        </FormProvider>
      </DialogContent>
    </ModalDialog>
  );
};
